<template>
  <div class="regulation-payment-content">
    <!-- Logout button -->
    <button
      class="regulation-sandbox-close"
      title="Logout"
      @click="logoutAndExit"
    >
      <span aria-hidden="true">&times;</span>
    </button>

    <div class="regulation-form-content">
      <!-- Warning banner -->
      <div class="regulation-warning-banner">
        <i class="fa-regular fa-triangle-exclamation"></i>
        <span>{{ $t('payment_regulation.banner_warning') }}</span>
      </div>

      <div class="regulation-form-title">{{ $t('payment_regulation.title') }}</div>
      <div class="regulation-form-subtitle">
        {{ $t('payment_regulation.subtitle_line1') }}
        <br>{{ $t('payment_regulation.subtitle_line2') }}
      </div>

      <!-- Period selector -->
      <div class="regulation-period">
        <label>{{ $t('payment_regulation.period_label') }}</label>
        <div class="regulation-period-selector">
          <button
            v-for="period in periodOptions"
            :key="period.value"
            type="button"
            class="period-btn"
            :class="{ active: selectedPeriod === period.value }"
            @click="onPeriodClick(period.value)"
          >
            {{ $t(period.labelKey) }}
          </button>
        </div>
      </div>

      <!-- Inline payment form -->
      <div class="payment-form">
        <div class="content-form">
          <label>{{ $t('payment_regulation.payment_label') }}</label>
          <!-- Card method -->
          <div class="card" :class="[{open: methodChoice == 'card'}]">
            <header @click="methodChoice = 'card'">
              <div class="circle active"><div></div></div>
              <p>{{ $t('dash.settings.billing.addMethod.method_card.title') }}</p>
              <div class="fa-brands fa-cc-visa"></div>
              <div class="fa-brands fa-cc-mastercard"></div>
            </header>
            <div class="content">
              <div class="container-input number-card">
                <label>{{ $t('dash.settings.billing.addMethod.method_card.number_card') }}</label>
                <div class="input">
                  <i class="fa-regular fa-credit-card"></i>
                  <div id="regulation-card-number" class="inputStripe"></div>
                </div>
                <div class="error"><i v-if="cardNumberError" class="fa-regular fa-circle-exclamation"></i>{{ cardNumberError }}</div>
              </div>
              <div class="container-input name-card">
                <label>{{ $t('dash.settings.billing.addMethod.method_card.name_card') }}</label>
                <div class="input">
                  <i class="fa-regular fa-circle-user"></i>
                  <input placeholder="ex. John Smith" v-model="nameCardValue">
                </div>
                <div class="error"><i v-if="cardNameError" class="fa-regular fa-circle-exclamation"></i>{{ cardNameError }}</div>
              </div>
              <div class="last-line">
                <div class="container-input expire-card">
                  <label>{{ $t('dash.settings.billing.addMethod.method_card.expire_card') }}</label>
                  <div class="input">
                    <i class="fa-regular fa-calendar"></i>
                    <div id="regulation-card-expiry" class="inputStripe"></div>
                  </div>
                </div>
                <div class="container-input key-card">
                  <label>{{ $t('dash.settings.billing.addMethod.method_card.security_card') }}</label>
                  <div class="input">
                    <i class="fa-regular fa-key"></i>
                    <div id="regulation-card-cvc" class="inputStripe"></div>
                  </div>
                </div>
              </div>
              <div class="error"><i v-if="stripeError || cardExpiryError || cardCvcError" class="fa-regular fa-circle-exclamation"></i>{{ stripeError || cardExpiryError || cardCvcError }}</div>
            </div>
          </div>

          <!-- PayPal -->
          <div class="card" :class="[{open: methodChoice == 'paypal'}]">
            <header @click="methodChoice = 'paypal'">
              <div class="circle" :class="{active: methodChoice == 'paypal'}"><div></div></div>
              <p>Paypal</p>
              <div class="fa-brands fa-cc-paypal"></div>
            </header>
            <div class="content" v-if="methodChoice == 'paypal'">
              <p class="paypal-info">{{ $t('payment_regulation.paypal_redirect') }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="regulation-navigation">
        <button
          type="button"
          class="regulation-btn regulation-btn-support"
          @click="contactSupport"
        >
          <i class="fa-regular fa-headset"></i>
          {{ $t('payment_regulation.contact_support') }}
        </button>

        <button
          type="button"
          class="regulation-btn regulation-btn-submit"
          :disabled="!canSubscribe || isLoading"
          :class="{ disabled: !canSubscribe || isLoading }"
          @click="handleSubscribe"
        >
          <template v-if="isLoading">{{ $t('payment_regulation.loading') }}</template>
          <template v-else>{{ $t('payment_regulation.submit_button') }}</template>
        </button>
      </div>
    </div>

    <div class="regulation-card-carousel">
      <!-- Chosen plan indicator -->
      <div class="carousel-chosen-label">
        <i class="fa-solid fa-chevrons-down"></i>
        <span>{{ $t('onboarding.payment.chosen_plan_label') }}</span>
        <i class="fa-solid fa-chevrons-down"></i>
      </div>

      <!-- Navigation arrow left -->
      <button
        v-show="selectedPlanIndex > 0"
        class="carousel-arrow carousel-arrow-left"
        @click="prevPlan"
      >
        <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.5 1L1.5 8L8.5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Card stack -->
      <div class="carousel-cards-wrapper">
        <div
          v-for="(cardPlan, index) in selectablePlans"
          :key="cardPlan.id"
          class="regulation-card"
          :class="{
            'card-active': index === selectedPlanIndex,
            'card-left': index < selectedPlanIndex,
            'card-right': index > selectedPlanIndex,
            'card-near': Math.abs(index - selectedPlanIndex) === 1,
            'card-far': Math.abs(index - selectedPlanIndex) >= 2
          }"
          :style="getCardStyle(index)"
          @click="selectPlan(index)"
        >
          <div class="regulation-card-header">
            <template v-if="cardPlan.id === 'custom'">{{ $t('dash.plan.card.button.contact') }}</template>
            <template v-else-if="cardPlan._planType === userCurrentPlanType">
              <i class="fa-regular fa-rotate-right"></i>
              {{ $t('payment_regulation.current_plan') }}
            </template>
            <template v-else>
              <i class="fa-regular fa-arrow-up"></i>
              {{ $t('payment_regulation.upgrade_plan') }}
            </template>
          </div>
          <div class="regulation-card-body">
            <!-- Standard plan card -->
            <template v-if="cardPlan.id !== 'custom'">
              <div class="plan-header">
                <h2>{{ cardPlan.name }}</h2>
                <div class="offer-container" v-if="getPlanOffer(cardPlan).savingsBadge || getPlanOffer(cardPlan).offerText">
                  <span v-if="getPlanOffer(cardPlan).savingsBadge" class="savings-badge">
                    {{ $t(getPlanOffer(cardPlan).savingsBadge) }}
                  </span>
                  <span v-if="getPlanOffer(cardPlan).offerText" class="offer-text">
                    {{ getPlanOffer(cardPlan).offerText }}
                  </span>
                </div>
              </div>

              <div class="price-display">
                <span class="price">{{ getPlanPrice(cardPlan) }}€ <sup>{{ $t('dash.plan.card.tax_label') }}</sup></span>
                <span class="period">&nbsp;{{ priceLabel }}</span>
              </div>

              <ul class="features-list">
                <li>
                  {{ $t('dash.plan.card.features.up_to') }}
                  <span class="feature-value">{{ cardPlan.maxSocialAccounts }}</span>
                  {{ $t('dash.plan.card.features.social_accounts') }}
                </li>
                <li>
                  {{ $t('dash.plan.card.features.up_to') }}
                  <span class="feature-value">{{ cardPlan.maxUsers }}</span>
                  {{ $t('dash.plan.card.features.users') }}
                </li>
                <li>
                  {{ $t('dash.plan.card.features.post_from') }}
                  <span class="feature-value">{{ cardPlan.maxSources }}</span>
                  {{ $t('dash.plan.card.features.sources') }}
                </li>
                <li
                  v-for="(featureKey, fIndex) in featureKeys"
                  :key="fIndex"
                  :class="{ 'not-included': !cardPlan.features[featureKey] }"
                >
                  {{ $t(featureLabels[featureKey]) }}
                </li>
              </ul>
            </template>

            <!-- Custom plan card -->
            <template v-else>
              <div class="contact-display">
                <span class="contact-text">
                  {{ $t('dash.plan.card.custom.question') }}
                </span>
                <span class="contact-text">
                  {{ $t('dash.plan.card.custom.answer') }}
                </span>
              </div>

              <ul class="features-list-contact">
                <li>{{ $t('payment_regulation.prefix') }} <span class="feature-value">{{ $t('payment_regulation.custom_pages') }}</span></li>
                <li>{{ $t('payment_regulation.prefix') }} <span class="feature-value">{{ $t('payment_regulation.custom_users') }}</span></li>
                <li>{{ $t('payment_regulation.prefix') }} <span class="feature-value">{{ $t('payment_regulation.custom_sources') }}</span></li>
                <li
                  v-for="(featureKey, fIndex) in featureKeys"
                  :key="fIndex"
                >
                  {{ $t(featureLabels[featureKey]) }}
                </li>
              </ul>

              <button
                type="button"
                class="btn-contact"
                @click.stop="contactSupport"
              >
                {{ $t('dash.plan.card.button.contact') }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Navigation arrow right -->
      <button
        v-show="selectedPlanIndex < selectablePlans.length - 1"
        class="carousel-arrow carousel-arrow-right"
        @click="nextPlan"
      >
        <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 1L8.5 8L1.5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Dots indicator -->
      <div class="carousel-dots">
        <span
          v-for="(cardPlan, index) in selectablePlans"
          :key="'dot-' + cardPlan.id"
          class="carousel-dot"
          :class="{ active: index === selectedPlanIndex }"
          @click="selectPlan(index)"
        ></span>
      </div>

      <!-- Downgrade notice -->
      <div class="regulation-downgrade-notice">
        <i class="fa-regular fa-circle-info"></i>
        {{ $t('payment_regulation.downgrade_notice') }}
      </div>
    </div>
  </div>
</template>

<script>
const { VUE_APP_STRIPE_KEY, VUE_APP_API_HOST } = process.env

import Subscriptions from '../mixins/Subscriptions.vue'
import User from '../mixins/User.vue'
import {
  PERIODS,
  PERIOD_OPTIONS,
  PERIOD_MULTIPLIERS,
  PERIOD_OFFERS,
  PERIOD_SAVED_MONTHS
} from '../dash/pricing/config/pricingConfig'
import { featureLabels, plansBasic } from '../dash/pricing/data/plans'

// Plan ID to subscription type mapping (backend expects numeric type)
const PLAN_TYPE_MAP = {
  starter: 1,
  standard: 2,
  premium: 3
}

// Reverse mapping: plan number → plan ID
const PLAN_ID_FROM_TYPE = {
  1: 'starter',
  2: 'standard',
  3: 'premium'
}

const CLIENT_SECRET_ROUTE = `${VUE_APP_API_HOST}/accounts/me/secret`

export default {
  name: 'PaymentRegulation',

  mixins: [Subscriptions, User],

  data() {
    return {
      // Plan & period
      selectedPeriod: PERIODS.ONE_YEAR,
      selectedPlanIndex: 0,
      periodOptions: PERIOD_OPTIONS,
      featureLabels,
      featureKeys: [
        'native',
        'planification',
        'moderation',
        'reporting',
        'collaboration'
      ],
      isLoading: false,

      // Payment method choice
      methodChoice: 'card',

      // Stripe instance & elements
      stripe: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,

      // Stripe element style
      stripeStyle: {
        base: {
          fontFamily: '"THICCCBOI"',
          lineHeight: '36.8px',
          fontSize: '13.6px',
          color: '#555E75',
          fontWeight: '600',
          '::placeholder': {
            fontWeight: '500',
            color: 'rgba(85, 94, 117, 0.34)'
          }
        },
        invalid: {
          color: '#FF5C5C'
        }
      },

      // Card validation state
      card: { cvc: false, number: false, expiry: false },

      // Form values
      nameCardValue: '',

      // Errors
      stripeError: '',
      cardCvcError: '',
      cardExpiryError: '',
      cardNumberError: '',
      cardNameError: '',

      // Stripe Setup Intent for PayPal
      clientSecret: null,
      clientSecretRoute: CLIENT_SECRET_ROUTE,

      apiRoute: `${VUE_APP_API_HOST}/accounts/me/payment-method`
    }
  },

  computed: {
    /**
     * The numeric plan type the user had before the payment issue
     */
    userCurrentPlanType() {
      return (this.user && this.user._premium && this.user._premium.plan) || 1
    },

    /**
     * Plans available: only the user's current plan and higher tiers (no downgrades).
     * The user's current plan is always first.
     */
    selectablePlans() {
      const currentPlanId = PLAN_ID_FROM_TYPE[this.userCurrentPlanType]
      const currentIdx = plansBasic.findIndex(p => p.id === currentPlanId)

      if (currentIdx === -1) return plansBasic

      // Return plans from current plan onwards (includes custom)
      return plansBasic.slice(currentIdx)
    },

    plan() {
      return this.selectablePlans[this.selectedPlanIndex] || this.selectablePlans[0]
    },

    periodMultiplier() {
      return PERIOD_MULTIPLIERS[this.selectedPeriod]
    },

    priceLabel() {
      if (this.selectedPeriod === PERIODS.MONTHLY) {
        return '/mois'
      }
    },

    canSubscribe() {
      if (!this.plan || this.plan.id === 'custom') return false
      if (this.methodChoice === 'card') return true
      if (this.methodChoice === 'paypal') return true
      return false
    }
  },

  async mounted() {
    // Always fetch fresh user data in case the account was reactivated from another profile
    await this.fetchUserInfo()
    // If the account is already active (reactivated elsewhere), redirect to dashboard
    if (this.user && this.user._premium && this.user._premium.active) {
      this.$router.replace({ name: 'dash' })
      return
    }
    this.setUpStripe()
    this.preselectPeriodFromUser()
  },

  methods: {
    /**
     * Logout: clear session and redirect to login
     */
    logoutAndExit() {
      localStorage.clear()
      window.$crisp.push(['do', 'session:reset'])
      location.href = '/'
    },

    /**
     * Initialize Stripe Elements
     */
    setUpStripe() {
      this.stripe = Stripe(VUE_APP_STRIPE_KEY) // eslint-disable-line
      const locale = localStorage.language
        ? localStorage.language.substring(0, 2)
        : 'fr'

      const elements = this.stripe.elements({ locale })

      // Use unique IDs to avoid conflicts with other Stripe instances
      this.cardNumber = elements.create('cardNumber', { style: this.stripeStyle })
      this.cardCvc = elements.create('cardCvc', { style: this.stripeStyle })
      this.cardExpiry = elements.create('cardExpiry', { style: this.stripeStyle })

      this.cardNumber.mount('#regulation-card-number')
      this.cardCvc.mount('#regulation-card-cvc')
      this.cardExpiry.mount('#regulation-card-expiry')

      this.listenForErrors()
    },

    listenForErrors() {
      this.cardNumber.addEventListener('change', (event) => {
        this.cardNumberError = (!event.complete && event.error) ? event.error.message : ''
        this.card.number = event.complete
      })
      this.cardExpiry.addEventListener('change', (event) => {
        this.cardExpiryError = (!event.complete && event.error) ? event.error.message : ''
        this.card.expiry = event.complete
      })
      this.cardCvc.addEventListener('change', (event) => {
        this.cardCvcError = (!event.complete && event.error) ? event.error.message : ''
        this.card.cvc = event.complete
      })
    },

    /**
     * Pre-select the period matching the user's previous subscription
     */
    preselectPeriodFromUser() {
      if (!this.user || !this.user._premium) return

      if (this.user._premium.is_2years) {
        this.selectedPeriod = PERIODS.TWO_YEARS
      } else if (this.user._premium.is_annual) {
        this.selectedPeriod = PERIODS.ONE_YEAR
      } else {
        this.selectedPeriod = PERIODS.MONTHLY
      }

      // First card (index 0) is always the user's current plan
      this.selectedPlanIndex = 0
    },

    selectPlan(index) {
      this.selectedPlanIndex = index
    },

    prevPlan() {
      if (this.selectedPlanIndex > 0) {
        this.selectPlan(this.selectedPlanIndex - 1)
      }
    },

    nextPlan() {
      if (this.selectedPlanIndex < this.selectablePlans.length - 1) {
        this.selectPlan(this.selectedPlanIndex + 1)
      }
    },

    getPlanPrice(cardPlan) {
      if (!cardPlan.priceMonthly) return ''
      const price = cardPlan.priceMonthly * this.periodMultiplier
      return price.toLocaleString('fr-FR')
    },

    getPlanOffer(cardPlan) {
      const savedMonths = PERIOD_SAVED_MONTHS[this.selectedPeriod]
      return {
        savingsBadge: PERIOD_OFFERS[this.selectedPeriod] || '',
        offerText: savedMonths && cardPlan.priceMonthly
          ? `Économisez ${cardPlan.priceMonthly * savedMonths}€ HT`
          : ''
      }
    },

    getCardStyle(index) {
      const offset = index - this.selectedPlanIndex
      const absOffset = Math.abs(offset)
      if (absOffset === 0) {
        return { transform: 'translateX(0) scale(1)', zIndex: 10, opacity: 1 }
      }
      const direction = offset > 0 ? 1 : -1
      return {
        transform: `translateX(${direction * 22 * absOffset}px) scale(${1 - absOffset * 0.06})`,
        zIndex: 10 - absOffset,
        opacity: absOffset <= 2 ? 0.4 - absOffset * 0.15 : 0
      }
    },

    onPeriodClick(period) {
      if (period !== this.selectedPeriod) {
        this.selectedPeriod = period
      }
    },

    /**
     * Validate and submit payment
     */
    handleSubscribe() {
      if (!this.canSubscribe || this.isLoading) return

      const planType = PLAN_TYPE_MAP[this.plan.id]
      if (!planType) return

      this.clearCardErrors()

      if (this.methodChoice === 'card') {
        if (!this.validateCard()) return
        this.submitCardPayment(planType)
      } else if (this.methodChoice === 'paypal') {
        this.submitPayPalPayment(planType)
      }
    },

    validateCard() {
      let valid = true
      if (!this.card.number) {
        valid = false
        this.cardNumberError = this.$t('dash.settings.billing.addMethod.method_card.number_card') + ' requis'
      }
      if (!this.card.cvc) {
        valid = false
        this.cardCvcError = 'CVC requis'
      }
      if (!this.card.expiry) {
        valid = false
        this.cardExpiryError = this.$t('dash.settings.billing.addMethod.method_card.expire_card') + ' requis'
      }
      if (!this.nameCardValue || this.nameCardValue.length === 0) {
        valid = false
        this.cardNameError = this.$t('dash.settings.billing.addMethod.method_card.name_card') + ' requis'
      }
      if (this.stripeError) valid = false
      return valid
    },

    /**
     * Card flow: fetch setup intent → confirm card setup → attach to customer → subscribe
     */
    async submitCardPayment(planType) {
      this.isLoading = true
      try {
        await this.fetchSecret()

        const { setupIntent, error } = await this.stripe.confirmCardSetup(
          this.clientSecret,
          {
            payment_method: {
              card: this.cardNumber,
              billing_details: { name: this.nameCardValue.trim() }
            }
          }
        )

        if (error) {
          this.handleStripeError(error)
          return
        }

        // Attach payment method to customer — wrap separately to surface API error messages
        let req
        try {
          req = await this.$http.put(this.apiRoute, {
            pm: setupIntent.payment_method,
            type: 'card'
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        } catch (httpErr) {
          console.error('[PaymentRegulation] Failed to save card payment method:', httpErr)
          const msg = httpErr && httpErr.body && httpErr.body.message
          this.showError(msg)
          return
        }

        if (!req.ok) {
          console.error('[PaymentRegulation] Save payment method responded not-ok:', req.status, req.body)
          this.showError(req.body && req.body.message)
          return
        }

        await this.processSubscription(planType)
      } catch (err) {
        console.error('[PaymentRegulation] Card payment error:', err)
        this.showError()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * PayPal flow: fetch setup intent → confirm PayPal → save method → subscribe
     */
    async submitPayPalPayment(planType) {
      this.isLoading = true
      try {
        await this.fetchSecret()

        const { setupIntent, error } = await this.stripe.confirmPayPalSetup(
          this.clientSecret,
          { return_url: location.href }
        )

        if (error) {
          this.handleStripeError(error)
          return
        }

        // Attach payment method to customer — wrap separately to surface API error messages
        let req
        try {
          req = await this.$http.put(this.apiRoute, {
            pm: setupIntent.payment_method,
            type: 'paypal'
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })
        } catch (httpErr) {
          console.error('[PaymentRegulation] Failed to save PayPal payment method:', httpErr)
          const msg = httpErr && httpErr.body && httpErr.body.message
          this.showError(msg)
          return
        }

        if (!req.ok) {
          console.error('[PaymentRegulation] Save payment method responded not-ok:', req.status, req.body)
          this.showError(req.body && req.body.message)
          return
        }

        await this.processSubscription(planType)
      } catch (err) {
        console.error('[PaymentRegulation] PayPal payment error:', err)
        this.showError()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Fetch Stripe Setup Intent secret and validate the response
     */
    async fetchSecret() {
      const { data } = await this.$http.get(
        this.clientSecretRoute + `?methodChoice=${this.methodChoice}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      )
      if (!data || !data.client_secret) {
        throw new Error('[PaymentRegulation] Server did not return a client_secret')
      }
      this.clientSecret = data.client_secret
    },

    /**
     * Process subscription after payment method is ready.
     * On success, refreshes user info and redirects to dashboard.
     */
    async processSubscription(planType) {
      try {
        if (this.selectedPlanIndex === 0) { // update (payment method only)
          await this.refreshUserInfos()
          this.$router.replace({ name: 'dash' })
        } else {
          const body = {
            type: planType,
            is_annual: this.selectedPeriod === PERIODS.ONE_YEAR,
            is_2years: this.selectedPeriod === PERIODS.TWO_YEARS,
            is_2026_plan: true,
            options: []
          }

          const res = await this.subscribePlan(body)

          if (!res || !res.body) {
            console.error('[PaymentRegulation] subscribePlan returned empty response:', res)
            this.showError()
            return
          }

          const { client_secret, success, message } = res.body
          let paymentSuccess = success

          // Handle 3D Secure confirmation if needed
          if (client_secret) {
            const { paymentIntent, error: confirmError } = await this.stripe.confirmCardPayment(client_secret)
            if (confirmError) {
              console.error('[PaymentRegulation] 3DS confirmCardPayment error:', confirmError)
              this.showError(confirmError.message)
              return
            }
            paymentSuccess = paymentIntent && paymentIntent.status === 'succeeded'
          }

          if (paymentSuccess) {
            // Refresh user info so needAddPaymentMethod is cleared
            await this.refreshUserInfos()

            this.$vs.dialog({
              color: 'success',
              title: this.$t('payment_regulation.success_title'),
              text: this.$t('payment_regulation.success_message'),
              acceptText: 'OK',
              accept: () => {
                this.$router.replace({ name: 'dash' })
              }
            })
          } else if (!message) {
            // Backend returned success: false with no error message — subscription may still be processing
            // (e.g. Stripe webhook not yet received). Poll user info until premium is active.
            console.warn('[PaymentRegulation] Subscription returned success:false with no message — polling for activation...')
            const activated = await this.pollSubscriptionActive()
            if (activated) {
              this.$vs.dialog({
                color: 'success',
                title: this.$t('payment_regulation.success_title'),
                text: this.$t('payment_regulation.success_message'),
                acceptText: 'OK',
                accept: () => {
                  this.$router.replace({ name: 'dash' })
                }
              })
            } else {
              console.error('[PaymentRegulation] Subscription not confirmed after polling. success:', success, 'message:', message)
              this.showError()
            }
          } else {
            console.error('[PaymentRegulation] Subscription not successful. success:', success, 'message:', message)
            this.showError(message)
          }
        }
      } catch (err) {
        console.error('[PaymentRegulation] Subscription error:', err)
        this.showError()
      }
    },

    /**
     * Poll user info until _premium.active is true (Stripe async processing).
     * Retries every 2s for up to 10s, then gives up.
     * @returns {Promise<boolean>}
     */
    async pollSubscriptionActive() {
      const MAX_ATTEMPTS = 5
      const DELAY_MS = 2000
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        await new Promise(resolve => setTimeout(resolve, DELAY_MS))
        await this.refreshUserInfos()
        if (this.isUserPremiumActive) {
          console.log(`[PaymentRegulation] Subscription activated after ${attempt} poll(s).`)
          return true
        }
        console.warn(`[PaymentRegulation] Poll ${attempt}/${MAX_ATTEMPTS}: subscription not yet active.`)
      }
      return false
    },

    /**
     * Handle Stripe errors with specific decline codes.
     * Sets the inline stripeError visible in the card form.
     */
    handleStripeError(error) {
      console.error('[PaymentRegulation] Stripe error:', error)
      if (error.decline_code) {
        const translationKey = `dash.settings.error.stripe.${error.decline_code}`
        const translated = this.$t(translationKey)
        if (translated !== translationKey) {
          this.stripeError = translated
          return
        }
      }
      this.stripeError = error.message || this.$t('dash.notifications.payment_failure')
    },

    /**
     * @param {string|null} message - Optional specific error message to display.
     */
    showError(message = null) {
      this.$vs.dialog({
        color: 'danger',
        title: this.$t('dash.notifications.error_title'),
        text: message || this.$t('dash.notifications.payment_failure'),
        acceptText: 'OK'
      })
    },

    clearCardErrors() {
      this.stripeError = ''
      this.cardCvcError = ''
      this.cardExpiryError = ''
      this.cardNumberError = ''
      this.cardNameError = ''
    },

    contactSupport() {
      window.$crisp.push(['do', 'chat:open'])
      window.$crisp.push([
        'do',
        'message:send',
        ['text', this.$t('payment_regulation.support_message')]
      ])
    }
  }
}
</script>

<style lang="scss" scoped>
@import './variables.scss';
// ========================================
// SANDBOX ESCAPE BUTTON
// ========================================

.regulation-sandbox-close {
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 100;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  color: #9ca3af;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
    color: #374151;
  }
}

// ========================================
// CHOSEN PLAN CAROUSEL LABEL
// ========================================

.carousel-chosen-label {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
  color: $tertiary-color;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  i {
    font-size: 12px;
    animation: bounce-down 1.4s ease-in-out infinite;
  }
}

@keyframes bounce-down {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

// ========================================
// PAYMENT REGULATION CONTENT CONTAINER
// ========================================

.regulation-payment-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  gap: 100px;
  padding: 60px;

  @media (max-width: 1200px) {
    flex-direction: column;
    gap: 20px;
  }
}

// ========================================
// WARNING BANNER
// ========================================

.regulation-warning-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 12px;
  padding: 14px 20px;
  margin-bottom: 1.5rem;
  font-size: 14px;
  font-weight: 600;
  color: #856404;

  i {
    font-size: 18px;
    flex-shrink: 0;
  }
}

// ========================================
// LEFT SECTION - PAYMENT FORM
// ========================================

.regulation-form-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2rem;

  .regulation-form-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 1rem;
  }

  .regulation-form-subtitle {
    font-size: 16px;
    color: #6B7280;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .regulation-period {
    display: flex;
    flex-direction: column;
    margin-bottom: 2rem;

    > label {
      font-size: 14px;
      font-weight: 500;
      color: #6B7280;
      margin-bottom: 0.75rem;
    }

    .regulation-period-selector {
      background: $bg-ui;
      border: 2px solid $bg-ui;
      display: inline-flex;
      justify-content: space-between;
      padding: 2px;
      border-radius: 20px;
      width: 360px;

      .period-btn {
        border: none;
        background: transparent;
        padding: 9px 35px;
        border-radius: 20px !important;
        font-size: 14px;
        font-weight: 500;
        color: #6B7280;
        transition: all 0.3s ease;
        cursor: pointer;

        &.active {
          background: $tertiary-color;
          border-radius: 20px !important;
          color: #fff;
        }

        &:hover:not(.active) {
          background: rgba(107, 31, 77, 0.1);
        }
      }
    }
  }
}

.regulation-navigation {
  display: flex;
  gap: 16px;
  margin-top: 1rem;
}

.regulation-btn {
  padding: 14px 32px;
  border-radius: 30px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.regulation-btn-support {
  background-color: white;
  color: #6B7280;
  border: 1px solid $border-ui;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    font-size: 16px;
  }

  &:hover {
    background-color: #f3f4f6;
    color: $primary-color;
  }
}

.regulation-btn-submit {
  background-color: $bg-ui-selected;
  color: $primary-color;

  &:hover:not(.disabled) {
    background-color: $primary-color;
    color: #fff;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// ========================================
// INLINE PAYMENT FORM
// ========================================

.payment-form {
  margin-bottom: 1.5rem;

  .content-form {
    display: flex;
    flex-direction: column;
    max-width: 900px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: #6B7280;
      margin-bottom: 0.75rem;
    }

    .card {
      width: 100%;
      border: solid transparent 2.4px;
      height: 46.4px;
      overflow: hidden;
      margin-bottom: 12px;

      header {
        display: grid;
        align-items: center;
        grid-template-columns: auto 1fr auto auto;
        cursor: pointer;

        .circle {
          border: solid transparent 1.6px;
          margin-left: 4px;
          margin-right: 8px;

          & > div {
            height: 16.8px;
            width: 16.8px;
            border-radius: 50%;
            border: solid #fff 4px;
          }
        }

        div.fa-brands {
          font-size: 26.4px;
          margin-left: 8px;
        }
      }

      &.open {
        height: auto;
        border: solid $primary-color 2.4px;

        header .circle {
          border: solid #fff 1.6px;

          & > div {
            border: solid $primary-color 4px;
          }
        }
      }

      .content {
        padding: 18.4px 16px 0 16px;
        display: flex;
        flex-direction: column;

        .paypal-info {
          font-size: 13.6px;
          color: #6B7280;
          font-weight: 500;
          padding-bottom: 16px;
        }

        .container-input {
          label {
            font-size: 13.6px;
            color: #6B7280;
            font-weight: 700;
            display: block;
            margin-bottom: 4px;
          }

          .input {
            width: 100%;
            display: flex;
            align-items: center;
            height: 36.8px;
            border-radius: 4px;
            border: 1.6px #F1EDF5 solid;
            padding: 0 9.6px;

            i {
              color: #AB6E93;
              margin-right: 9.6px;
              font-size: 16.8px;
            }

            .inputStripe {
              width: 0;
              flex-grow: 1;
              height: 100%;
            }

            input {
              width: 0;
              flex-grow: 1;
              height: 100%;
              outline: none;
              border: none;
              font-size: 13.6px;
              color: #555E75;
              font-weight: 600;

              &::placeholder {
                font-weight: 500;
                opacity: 0.34;
              }
            }
          }
        }

        .last-line {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-gap: 8px;

          .container-input {
            margin-bottom: 0;
          }
        }

        .error {
          color: #FF5C5C;
          font-size: 12px;
          font-weight: 600;
          height: 20px;
          margin-bottom: 4px;
          display: flex;
          align-items: center;

          i {
            margin-right: 4px;
            font-size: 10.4px;
          }
        }
      }
    }
  }
}

// ========================================
// RIGHT SECTION - CARD CAROUSEL
// ========================================

.regulation-card-carousel {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 440px;
  margin-top: 200px;

  @media (max-width: 500px) {
    min-width: 380px;
  }

  @media (max-width: 1200px) {
    margin-top: 60px;
  }
}

.carousel-cards-wrapper {
  position: relative;
  width: 390px;
  height: 480px;

  @media (max-width: 400px) {
    width: 360px;
  }
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 20;
  width: 36px;
  height: 36px;
  border: 2px solid $bg-ui;
  border-radius: 50%;
  background: #fff;
  color: $tertiary-color;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: $bg-ui;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &-left {
    left: -40px;
  }

  &-right {
    right: -40px;
  }
}

.carousel-dots {
  display: flex;
  gap: 8px;
  margin-top: 20px;
}

.carousel-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d1d5db;
  cursor: pointer;
  transition: all 0.2s ease;

  &.active {
    background: $tertiary-color;
    transform: scale(1.2);
  }

  &:hover:not(.active) {
    background: #9ca3af;
  }
}

.regulation-card {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 390px;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(107, 43, 89, 0.10);
  border: 3px solid $tertiary-color;
  background: $tertiary-color;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  pointer-events: none;

  &.card-active {
    box-shadow: 0 25px 50px -10px rgba(107, 43, 89, 0.15);
  }

  @media (max-width: 400px) {
    width: 360px;
  }

  .regulation-card-header {
    background: $tertiary-color;
    color: #fff;
    text-align: center;
    padding: 15px;
    font-size: 16px;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    i {
      font-size: 14px;
    }
  }

  .regulation-card-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 17px;
    padding: 25px;
    background: #fff;

    .plan-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      h2 {
        font-size: 30px;
        font-weight: 600;
        margin: 0;
      }
    }

    .offer-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
    }

    .savings-badge {
      background: $secondary-color;
      color: #fff;
      font-size: 12px;
      padding: 5px 12px;
      border-radius: 20px;
    }

    .offer-text {
      color: $secondary-color;
      font-size: 13px;
      font-weight: 800;
    }

    .price-display,
    .contact-display {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 20px;
    }

    .price-display {
      color: $primary-text-color;
    }

    .contact-display {
      color: $tertiary-color;
    }

    .contact-text {
      font-size: 16px;
      color: $tertiary-color;
      display: block;

      &:first-child {
        margin-bottom: 6px;
      }
    }

    .btn-contact {
      width: 100%;
      padding: 12px 24px;
      background: $primary-color;
      color: #fff;
      border: none;
      border-radius: 20px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
      pointer-events: auto;

      &:hover {
        background: darken($primary-color, 8%);
      }
    }

    .price {
      font-size: 24px;
      font-weight: 700;
      color: $primary-text-color;

      sup {
        font-size: 12px;
        font-weight: 600;
      }
    }

    .period {
      font-size: 18px;
      font-weight: 400;
      color: $secondary-text-color;
    }

    .features-list,
    .features-list-contact {
      list-style: none;
      padding: 0;
      margin: 0 0 30px 0;

      li {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 10px;
        gap: 5px;

        &::before {
          content: '✓';
          color: $tertiary-color;
          font-weight: 800;
          font-size: 16px;
          margin-right: 8px;
        }

        &.not-included {
          color: #b2b3b7;

          &::before {
            content: '✕';
            color: #b2b3b7;
            font-size: 12px;
          }
        }
      }
    }

    .features-list-contact {
      margin-bottom: 10px;
    }

    .feature-value {
      font-weight: 600;
    }
  }
}

// ========================================
// DOWNGRADE NOTICE
// ========================================

.regulation-downgrade-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 10px 16px;
  background: #f3f4f6;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;

  i {
    font-size: 14px;
    flex-shrink: 0;
    color: $secondary-color;
  }
}
</style>
